/* eslint-disable react-refresh/only-export-components -- This module
   deliberately exports helpers/types alongside its component(s). The cost
   is dev-only: Vite falls back to a full reload for this module instead of
   a hot swap. Splitting the non-component exports into a sibling file is a
   worthwhile follow-up, but it moves import sites across the app, so it is
   not bundled into the lint-debt sweep. The rule stays ON for every other
   file, which is where it earns its keep. */
import { useState } from "react";
import { CheckCircle2, AlertCircle, Loader2, Database } from "lucide-react";

import { portalFetch } from "@/lib/portalApi";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePermissions } from "@/hooks/usePermissions";
import { OdbcFieldError } from "@/components/portal/shared/OdbcFieldError";
import {
  hasOdbcFieldError,
  odbcFieldErrors,
  odbcInputProps,
} from "@/lib/odbcConnectionFields";

export type SqlConnectionValues = {
  host: string;
  database: string;
  username: string;
  password: string;
  schema: string;
};

/**
 * Build the source.connectionString persisted alongside the connector.
 *
 * SqlServer uses ADO.NET SqlClient form (``Data Source=host[,port];
 * Initial Catalog=...;User Id=...;Password=...``) — what the C# engine's
 * SqlServerSourceConnector has always consumed.
 *
 * MySql uses MySqlConnector's native form (``Server=host;Port=nnn;
 * Database=...;Uid=...;Pwd=...``) because MySqlConnector does NOT parse
 * an ADO.NET-style embedded ``host:port`` inside ``Data Source=``, so a
 * connector stored in SqlClient format fails at refresh time with
 * "Unable to connect to any of the specified MySQL hosts".
 *
 * Exported for parent callers who also persist the connection string
 * (e.g. the edit properties flow).
 */
export function buildSqlConnectionString(
  sourceType: "SqlServer" | "MySql",
  values: Pick<
    SqlConnectionValues,
    "host" | "database" | "username" | "password"
  >,
): string {
  if (sourceType === "MySql") {
    const { host, port } = splitHostPort(values.host);
    const portSegment = port ? `Port=${port};` : "";
    return (
      `Server=${host};${portSegment}Database=${values.database};` +
      `Uid=${values.username};Pwd=${values.password}`
    );
  }
  return (
    `Data Source=${values.host};Initial Catalog=${values.database};` +
    `User Id=${values.username};Password=${values.password}`
  );
}

function splitHostPort(host: string): { host: string; port: string | null } {
  const idx = host.lastIndexOf(":");
  if (idx === -1) return { host, port: null };
  const maybePort = host.slice(idx + 1);
  if (!/^\d+$/.test(maybePort)) return { host, port: null };
  return { host: host.slice(0, idx), port: maybePort };
}

type SqlConnectionWithTestProps = {
  label: string;
  /**
   * Which SQL flavour the target DP should use. Threaded through to the
   * test-connection endpoint so it picks the aioodbc vs aiomysql helper.
   * Defaults to SqlServer for back-compat.
   */
  sourceType?: "SqlServer" | "MySql";
  values: SqlConnectionValues;
  onFieldChange: (field: keyof SqlConnectionValues, value: string) => void;
  onConnectionTested?: (result: {
    success: boolean;
    connectionString: string | null;
    existingSchemas: string[];
  }) => void;
  disabled?: boolean;
};

export function SqlConnectionWithTest({
  label,
  sourceType = "SqlServer",
  values,
  onFieldChange,
  onConnectionTested,
  disabled,
}: SqlConnectionWithTestProps) {
  // Test Connection dials a host the user types: org admin and above only (dp #2438 / #2439).
  const { bypassesGroups: canProbeConnection } = usePermissions();
  const { token } = useAuth();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<"success" | "error" | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [existingSchemas, setExistingSchemas] = useState<string[]>([]);

  // The server refuses `;` `{` `}` in these unless the type is MySql /
  // Postgres / Firebird (`@/lib/odbcConnectionFields`).
  const odbcErrors = odbcFieldErrors(values, sourceType);
  const canTest =
    values.host.trim() !== "" &&
    values.database.trim() !== "" &&
    values.username.trim() !== "" &&
    values.password.trim() !== "" &&
    !hasOdbcFieldError(odbcErrors);

  const handleTest = async () => {
    setTesting(true);
    setError(null);

    try {
      const result = await portalFetch<{
        success: boolean;
        errorMessage?: string;
        existingSchemas?: string[];
      }>("/api/v1/gateway/business2cloud/api/v1/database/test-connection", {
        method: "POST",
        token: token ?? undefined,
        body: {
          host: values.host.trim(),
          database: values.database.trim(),
          username: values.username.trim(),
          password: values.password.trim(),
          type: sourceType,
        },
      });

      if (result.success) {
        const schemas = result.existingSchemas ?? [];
        setTestResult("success");
        setExistingSchemas(schemas);

        const connectionString = buildSqlConnectionString(sourceType, values);

        // Auto-select first schema if none set
        if (!values.schema && schemas.length > 0) {
          onFieldChange("schema", schemas[0]);
        }

        onConnectionTested?.({
          success: true,
          connectionString,
          existingSchemas: schemas,
        });
      } else {
        setTestResult("error");
        setError(result.errorMessage || "Connection failed");
        onConnectionTested?.({
          success: false,
          connectionString: null,
          existingSchemas: [],
        });
      }
    } catch (err) {
      setTestResult("error");
      setError(err instanceof Error ? err.message : "Connection failed");
      onConnectionTested?.({
        success: false,
        connectionString: null,
        existingSchemas: [],
      });
    } finally {
      setTesting(false);
    }
  };

  // Reset test result when connection fields change
  const handleFieldChange = (
    field: keyof SqlConnectionValues,
    value: string,
  ) => {
    onFieldChange(field, value);
    if (field !== "schema") {
      setTestResult(null);
      setExistingSchemas([]);
      setError(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Host */}
      <div className="space-y-2">
        <Label htmlFor={`${label}-host`}>{label} Server</Label>
        <Input
          id={`${label}-host`}
          placeholder="e.g., server.database.windows.net"
          value={values.host}
          onChange={(e) => handleFieldChange("host", e.target.value)}
          disabled={disabled || testing}
          {...odbcInputProps(`${label}-host-error`, odbcErrors.host)}
        />
        <OdbcFieldError id={`${label}-host-error`} message={odbcErrors.host} />
      </div>

      {/* Database */}
      <div className="space-y-2">
        <Label htmlFor={`${label}-database`}>{label} Database</Label>
        <Input
          id={`${label}-database`}
          placeholder="e.g., mydatabase"
          value={values.database}
          onChange={(e) => handleFieldChange("database", e.target.value)}
          disabled={disabled || testing}
          {...odbcInputProps(`${label}-database-error`, odbcErrors.database)}
        />
        <OdbcFieldError
          id={`${label}-database-error`}
          message={odbcErrors.database}
        />
      </div>

      {/* Username / Password */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${label}-username`}>Username</Label>
          <Input
            id={`${label}-username`}
            placeholder="Username"
            value={values.username}
            onChange={(e) => handleFieldChange("username", e.target.value)}
            disabled={disabled || testing}
            {...odbcInputProps(`${label}-username-error`, odbcErrors.username)}
          />
          <OdbcFieldError
            id={`${label}-username-error`}
            message={odbcErrors.username}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${label}-password`}>Password</Label>
          <Input
            id={`${label}-password`}
            type="password"
            placeholder="Password"
            value={values.password}
            onChange={(e) => handleFieldChange("password", e.target.value)}
            disabled={disabled || testing}
            autoComplete="off"
            {...odbcInputProps(`${label}-password-error`, odbcErrors.password)}
          />
          <OdbcFieldError
            id={`${label}-password-error`}
            message={odbcErrors.password}
          />
        </div>
      </div>

      {/* Test Connection */}
      <div className="flex items-center gap-3">
        {canProbeConnection && (
          <Button
            type="button"
            variant="outline"
            onClick={handleTest}
            disabled={!canTest || disabled || testing}
          >
            {testing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Database className="mr-2 h-4 w-4" />
                Test Connection
              </>
            )}
          </Button>
        )}

        {testResult === "success" && (
          <div className="flex items-center gap-2 text-sm text-emerald-600">
            <CheckCircle2 className="h-4 w-4" />
            Connection successful
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-none text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Schema dropdown (shown after successful test) */}
      {testResult === "success" && (
        <div className="border-t border-slate-200 pt-4 space-y-2">
          <Label htmlFor={`${label}-schema`}>Schema</Label>
          {existingSchemas.length > 0 ? (
            <Select
              value={values.schema}
              onValueChange={(v) => onFieldChange("schema", v)}
              disabled={disabled}
            >
              <SelectTrigger id={`${label}-schema`}>
                <SelectValue placeholder="Select a schema" />
              </SelectTrigger>
              <SelectContent>
                {existingSchemas.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <>
              <Input
                id={`${label}-schema`}
                placeholder="dbo"
                value={values.schema}
                onChange={(e) => onFieldChange("schema", e.target.value)}
                disabled={disabled}
              />
              <p className="text-xs text-slate-500">
                No existing schemas found. Enter a schema name.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
